import React from "react";
import { Grid3X3, Box, MapPin, Camera, GitMerge, Spline, Plus } from "lucide-react";
import {
  JointIcon,
  ExplosionIcon,
  BoxGeomIcon,
  SphereGeomIcon,
  CapsuleGeomIcon,
  CylinderGeomIcon,
  SlideJointTypeIcon,
  BallJointTypeIcon,
} from "../../ui/Icons";
import Tooltip from "../../ui/Tooltip";
import OrderedIconActionContextRow from "../../ui/OrderedIconActionContextRow";
import { NON_FREE_JOINT_TYPE_OPTIONS } from "../../../utils/jointTypeValidation";

export default function SceneGraphCanvasContextMenu({
  addNewSubmenuOpen,
  setAddNewSubmenuOpen,
  addJointKindSubmenuOpen,
  setAddJointKindSubmenuOpen,
  addGeomKindSubmenuOpen,
  setAddGeomKindSubmenuOpen,
  addVisualGeomKindSubmenuOpen,
  setAddVisualGeomKindSubmenuOpen,
  isPhysicsRunning,
  worldRootTargetBodyUuid,
  handleAddRootBody,
  handleAddJointToBody,
  handleAddSiteToBody,
  handleAddGeomToBody,
  handleAddVisualGeomToBody,
  handleAddConstraintForBody,
  handleAddTendonForJoint,
  handleAddFixedWorldCamera,
  getPreferredTendonJointForBody,
}) {
  return (
    <>
      <div className="px-2 py-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wide">
        World root
      </div>
      <div className="px-2 pb-1.5">
        <OrderedIconActionContextRow
          activeSubmenuKey={addNewSubmenuOpen ? "addNew" : null}
          setActiveSubmenuKey={(key) => {
            const nextOpen = key === "addNew";
            setAddNewSubmenuOpen(nextOpen);
            if (!nextOpen) {
              setAddJointKindSubmenuOpen(false);
              setAddGeomKindSubmenuOpen(false);
              setAddVisualGeomKindSubmenuOpen(false);
            }
          }}
          submenuClassName="mt-1 rounded-md border border-gray-600 p-1 shadow-xl"
          submenuStyle={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
          actions={[
            {
              key: "addNew",
              label: "Add new",
              icon: <Plus className="w-4 h-4" aria-hidden="true" />,
              disabled: isPhysicsRunning,
              disabledByPhysics: isPhysicsRunning,
              onDefaultClick: () => {
                if (isPhysicsRunning) return;
                handleAddRootBody();
              },
              submenu: !isPhysicsRunning ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-1">
                    <Tooltip content="Body">
                      <button
                        type="button"
                        aria-label="Body"
                        onClick={handleAddRootBody}
                        onMouseEnter={() => {
                          setAddJointKindSubmenuOpen(false);
                          setAddGeomKindSubmenuOpen(false);
                          setAddVisualGeomKindSubmenuOpen(false);
                        }}
                        className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                      >
                        <Box className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Visual">
                      <button
                        type="button"
                        aria-label="Visual"
                        aria-disabled={isPhysicsRunning}
                        onClick={() => {
                          if (isPhysicsRunning) return;
                          handleAddVisualGeomToBody(null, "box", {
                            forceNewRoot: true,
                          });
                        }}
                        onMouseEnter={() => {
                          if (isPhysicsRunning) return;
                          setAddVisualGeomKindSubmenuOpen(true);
                          setAddJointKindSubmenuOpen(false);
                          setAddGeomKindSubmenuOpen(false);
                        }}
                        className={`h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center ${
                          !isPhysicsRunning
                            ? "text-gray-200 hover:bg-gray-700/90"
                            : "text-gray-300 opacity-60 cursor-not-allowed"
                        }`}
                      >
                        <Grid3X3 className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Joint">
                      <button
                        type="button"
                        aria-label="Joint"
                        aria-disabled={isPhysicsRunning}
                        onClick={() => {
                          if (isPhysicsRunning) return;
                          handleAddJointToBody(null, "hinge", { forceNewRoot: true });
                        }}
                        onMouseEnter={() => {
                          if (isPhysicsRunning) return;
                          setAddJointKindSubmenuOpen(true);
                          setAddGeomKindSubmenuOpen(false);
                          setAddVisualGeomKindSubmenuOpen(false);
                        }}
                        className={`h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center ${
                          !isPhysicsRunning
                            ? "text-gray-200 hover:bg-gray-700/90"
                            : "text-gray-300 opacity-60 cursor-not-allowed"
                        }`}
                      >
                        <JointIcon className="w-4 h-4" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Site">
                      <button
                        type="button"
                        aria-label="Site"
                        onClick={() => handleAddSiteToBody(null)}
                        onMouseEnter={() => {
                          setAddJointKindSubmenuOpen(false);
                          setAddGeomKindSubmenuOpen(false);
                          setAddVisualGeomKindSubmenuOpen(false);
                        }}
                        className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                      >
                        <MapPin className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </Tooltip>
                    <Tooltip content="Collision">
                      <button
                        type="button"
                        aria-label="Collision"
                        aria-disabled={isPhysicsRunning}
                        onClick={() => {
                          if (isPhysicsRunning) return;
                          handleAddGeomToBody(null, "sphere", { forceNewRoot: true });
                        }}
                        onMouseEnter={() => {
                          if (isPhysicsRunning) return;
                          setAddGeomKindSubmenuOpen(true);
                          setAddJointKindSubmenuOpen(false);
                          setAddVisualGeomKindSubmenuOpen(false);
                        }}
                        className={`h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center ${
                          !isPhysicsRunning
                            ? "text-gray-200 hover:bg-gray-700/90"
                            : "text-gray-300 opacity-60 cursor-not-allowed"
                        }`}
                      >
                        <ExplosionIcon className="w-4 h-4" />
                      </button>
                    </Tooltip>
                    {worldRootTargetBodyUuid ? (
                      <Tooltip content="Constraint">
                        <button
                          type="button"
                          aria-label="Constraint"
                          aria-disabled={isPhysicsRunning}
                          onClick={() => {
                            if (isPhysicsRunning) return;
                            handleAddConstraintForBody(worldRootTargetBodyUuid);
                          }}
                          onMouseEnter={() => {
                            setAddJointKindSubmenuOpen(false);
                            setAddGeomKindSubmenuOpen(false);
                            setAddVisualGeomKindSubmenuOpen(false);
                          }}
                          className={`h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center ${
                            !isPhysicsRunning
                              ? "text-gray-200 hover:bg-gray-700/90"
                              : "text-gray-300 opacity-60 cursor-not-allowed"
                          }`}
                        >
                          <GitMerge className="w-4 h-4" aria-hidden="true" />
                        </button>
                      </Tooltip>
                    ) : null}
                    {(() => {
                      const tendonJointUuid =
                        getPreferredTendonJointForBody(worldRootTargetBodyUuid);
                      const tendonDisabled = !tendonJointUuid;
                      return (
                        <Tooltip
                          content={
                            tendonDisabled
                              ? "Add a hinge or slide joint before adding a tendon"
                              : "Tendon"
                          }
                        >
                          <button
                            type="button"
                            aria-label="Tendon"
                            aria-disabled={tendonDisabled}
                            onClick={() => {
                              if (tendonDisabled) return;
                              handleAddTendonForJoint(tendonJointUuid);
                            }}
                            onMouseEnter={() => {
                              setAddJointKindSubmenuOpen(false);
                              setAddGeomKindSubmenuOpen(false);
                              setAddVisualGeomKindSubmenuOpen(false);
                            }}
                            className={`h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center ${
                              tendonDisabled
                                ? "text-gray-300 opacity-60 cursor-not-allowed"
                                : "text-gray-200 hover:bg-gray-700/90"
                            }`}
                          >
                            <Spline className="w-4 h-4" aria-hidden="true" />
                          </button>
                        </Tooltip>
                      );
                    })()}
                    <Tooltip content="Fixed world camera">
                      <button
                        type="button"
                        aria-label="Fixed world camera"
                        onClick={() => handleAddFixedWorldCamera()}
                        onMouseEnter={() => {
                          setAddJointKindSubmenuOpen(false);
                          setAddGeomKindSubmenuOpen(false);
                          setAddVisualGeomKindSubmenuOpen(false);
                        }}
                        className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                      >
                        <Camera className="w-4 h-4" aria-hidden="true" />
                      </button>
                    </Tooltip>
                  </div>
                  {addJointKindSubmenuOpen ? (
                    <div
                      className="rounded-md border border-gray-600 p-1 shadow-xl"
                      style={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
                    >
                      <div className="flex items-center gap-1">
                        {NON_FREE_JOINT_TYPE_OPTIONS.map(({ kind, label }) => {
                          const Icon =
                            kind === "slide"
                              ? SlideJointTypeIcon
                              : kind === "ball"
                                ? BallJointTypeIcon
                                : JointIcon;
                          return (
                            <Tooltip key={kind} content={label}>
                              <button
                                type="button"
                                aria-label={label}
                                onClick={() =>
                                  handleAddJointToBody(null, kind, {
                                    forceNewRoot: true,
                                  })
                                }
                                className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                              >
                                <Icon className="w-4 h-4" aria-hidden="true" />
                              </button>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </div>
                  ) : null}
                  {addVisualGeomKindSubmenuOpen ? (
                    <div
                      className="rounded-md border border-gray-600 p-1 shadow-xl"
                      style={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
                    >
                      <div className="flex items-center gap-1">
                        {[
                          { kind: "box", label: "Box", Icon: BoxGeomIcon },
                          { kind: "sphere", label: "Sphere", Icon: SphereGeomIcon },
                          { kind: "capsule", label: "Capsule", Icon: CapsuleGeomIcon },
                          {
                            kind: "cylinder",
                            label: "Cylinder",
                            Icon: CylinderGeomIcon,
                          },
                          { kind: "mesh", label: "Mesh", Icon: Grid3X3 },
                        ].map(({ kind, label, Icon }) => (
                          <Tooltip key={kind} content={label}>
                            <button
                              type="button"
                              aria-label={label}
                              onClick={() =>
                                handleAddVisualGeomToBody(null, kind, {
                                  forceNewRoot: true,
                                })
                              }
                              className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                            >
                              <Icon className="w-4 h-4" aria-hidden="true" />
                            </button>
                          </Tooltip>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  {addGeomKindSubmenuOpen ? (
                    <div
                      className="rounded-md border border-gray-600 p-1 shadow-xl"
                      style={{ backgroundColor: "rgba(40, 40, 40, 0.95)" }}
                    >
                      <div className="flex items-center gap-1">
                        {[
                          { kind: "box", label: "Box", Icon: BoxGeomIcon },
                          { kind: "sphere", label: "Sphere", Icon: SphereGeomIcon },
                          { kind: "capsule", label: "Capsule", Icon: CapsuleGeomIcon },
                          {
                            kind: "cylinder",
                            label: "Cylinder",
                            Icon: CylinderGeomIcon,
                          },
                        ].map(({ kind, label, Icon }) => (
                          <Tooltip key={kind} content={label}>
                            <button
                              type="button"
                              aria-label={label}
                              onClick={() =>
                                handleAddGeomToBody(null, kind, {
                                  forceNewRoot: true,
                                })
                              }
                              className="h-8 w-8 rounded-sm text-sm transition-colors flex items-center justify-center text-gray-200 hover:bg-gray-700/90"
                            >
                              <Icon className="w-4 h-4" aria-hidden="true" />
                            </button>
                          </Tooltip>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null,
            },
          ]}
        />
      </div>
    </>
  );
}
